import { supa } from '../components/firebase';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, OpenInWindow, Calendar, GraphUp, Wallet, User, StatsUpSquare } from 'iconoir-react';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title 
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';


ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title
);

function Analisi() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false)
  const [dal, setDal] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 2).toISOString().split('T')[0])
  const [al, setAl] = useState(new Date().toISOString().split('T')[0])
  const [lezioniPerPersonalTrainer, setLezioniPerPersonalTrainer] = useState({})
  const [versamenti, setVersamenti] = useState([])
  const [pacchettiAcquistati, setPacchettiAcquistati] = useState([])
  
  // Filtri
  const [filtroPacchetti, setFiltroPacchetti] = useState('tutti') // tutti, personal, pilates, consulenza
  const [filtroVersamenti, setFiltroVersamenti] = useState('tutti')

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([getLezioni(), getPacchettiAcquistati(), getVersamenti()]);
    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
    }
    setLoading(false);
  };

  const getLezioni = async () => {
    try {
      const { data, error } = await supa
        .from('lezioni')
        .select('*,personalTrainer(*)')
        .gte('data', dal)
        .lte('data', al);

      if (error) throw error;

      const grouped = data?.reduce((r, a) => {
        if (a.personalTrainer) {
          const key = `${a.personalTrainer.nome} ${a.personalTrainer.cognome}`;
          r[key] = [...(r[key] || []), a];
        }
        return r;
      }, {});

      setLezioniPerPersonalTrainer(grouped || {});
    } catch (error) {
      console.error('Errore nel caricamento lezioni:', error);
    }
  };

  const getPacchettiAcquistati = async () => {
    try {
      const { data, error } = await supa
        .from('pacchettiAcquistati')
        .select('*,utenti(*)')
        .gte('created_at', dal)
        .lte('created_at', al);

      if (error) throw error;

      data?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setPacchettiAcquistati(data || []);
    } catch (error) {
      console.error('Errore nel caricamento pacchetti:', error);
    }
  };

  const getVersamenti = async () => {
    try {
      const { data, error } = await supa
        .from('versamenti')
        .select('*,utenti(*),pacchettiAcquistati(*,pacchetti(*))')
        .gte('data', dal)
        .lte('data', al);

      if (error) throw error;

      data?.sort((a, b) => new Date(b.data) - new Date(a.data));
      setVersamenti(data || []);
    } catch (error) {
      console.error('Errore nel caricamento versamenti:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dal, al]);

  const refresh = () => {
    fetchData();
  };

  const pieData = {
    labels: lezioniPerPersonalTrainer && Object.keys(lezioniPerPersonalTrainer),
    datasets: [
      {
        label: 'Lezioni effettuate',
        data: lezioniPerPersonalTrainer && Object.keys(lezioniPerPersonalTrainer).map((key) => lezioniPerPersonalTrainer[key].length),
        backgroundColor: [
          '#519DE9', '#7CC674', '#73C5C5', '#8481DD',
          '#F6D173', '#EF9234', '#A30000', '#B8BBBE',
        ],
        borderColor: [
          '#519DE9', '#7CC674', '#73C5C5', '#8481DD',
          '#F6D173', '#EF9234', '#A30000', '#B8BBBE',
        ],
        borderWidth: 2,
      },
    ],
  };

  // Calcoli per le statistiche
  const totaleLezioni = Object.values(lezioniPerPersonalTrainer).reduce((acc, curr) => acc + curr.length, 0);
  const totaleGuadagniPersonalTrainer = Object.entries(lezioniPerPersonalTrainer).reduce(
    (acc, [_, lezioni]) => acc + (lezioni.length * (lezioni[0]?.personalTrainer?.costo || 0)), 
    0
  );
  const totalePacchettiAcquistati = pacchettiAcquistati?.reduce((acc, item) => acc + item.totale, 0) || 0;
  const totaleVersamenti = versamenti?.reduce((acc, item) => acc + item.importo, 0) || 0;
  
  // Breakdown per tipo di pacchetto
  const pacchettiPersonal = pacchettiAcquistati?.filter(p => p.tipo === 0) || [];
  const pacchettiPilates = pacchettiAcquistati?.filter(p => p.tipo === 1) || [];
  const pacchettiConsulenza = pacchettiAcquistati?.filter(p => p.tipo === 2) || [];

  // Filtro pacchetti
  const pacchettiFiltered = pacchettiAcquistati?.filter(p => {
    if (filtroPacchetti === 'personal') return p.tipo === 0;
    if (filtroPacchetti === 'pilates') return p.tipo === 1;
    if (filtroPacchetti === 'consulenza') return p.tipo === 2;
    return true;
  }) || [];

  // Filtro versamenti
  const versamentiFiltered = versamenti?.filter(v => {
    if (filtroVersamenti === 'personal') return v.pacchettiAcquistati?.tipo === 0;
    if (filtroVersamenti === 'pilates') return v.pacchettiAcquistati?.tipo === 1;
    if (filtroVersamenti === 'consulenza') return v.pacchettiAcquistati?.tipo === 2;
    return true;
  }) || [];

  // Calcoli finanziari avanzati
  const totaleIncassato = versamentiFiltered.reduce((acc, v) => acc + v.importo, 0);
  const costoPersonalTrainer = totaleGuadagniPersonalTrainer; // Costo che paghi ai PT
  const margineNetto = totaleIncassato - costoPersonalTrainer; // Profitto netto della palestra
  const marginePercentuale = totaleIncassato > 0 ? ((margineNetto / totaleIncassato) * 100) : 0;

  // Calcolo profitto per PT con ricavi e costi
  const profittoPerPT = Object.entries(lezioniPerPersonalTrainer).map(([nome, lezioni]) => {
    const costoPerLezione = lezioni[0]?.personalTrainer?.costo || 0;
    const costoTotalePT = lezioni.length * costoPerLezione;
    const ptId = lezioni[0]?.personalTrainer?.id; // Salva l'ID del PT
    
    // Calcola il ricavo generato da questo PT in base alle lezioni che ha tenuto
    let ricavoGenerato = 0;    lezioni.forEach((lezione) => {
      const pacchettoAcquistato = lezione.pacchettiAcquistati || lezione.pacchettoAcquistato;
      
      if (pacchettoAcquistato) {
        // Prendi il numero di lezioni dal pacchetto base (campo: num_lezioni)
        const numeroLezioni = pacchettoAcquistato.pacchetti?.num_lezioni || pacchettoAcquistato.sedute_pacchetto;
        const prezzoTotale = pacchettoAcquistato.totale;
        
        if (numeroLezioni > 0 && prezzoTotale > 0) {
          // Calcola il valore per singola lezione del pacchetto
          const valoreLezionePacchetto = prezzoTotale / numeroLezioni;
          ricavoGenerato += valoreLezionePacchetto;
        }
      }
    });
    
    const profittoNetto = ricavoGenerato - costoTotalePT;
    
    return {
      id: ptId,
      nome,
      lezioni: lezioni.length,
      costoPerLezione: costoPerLezione,
      costoTotale: costoTotalePT,
      ricavoGenerato: ricavoGenerato,
      profittoNetto: profittoNetto
    };
  });

  // Grafico a torta per lezioni per Personal Trainer
  const pieChartData = {
    labels: Object.keys(lezioniPerPersonalTrainer),
    datasets: [{
      label: 'Lezioni effettuate',
      data: Object.values(lezioniPerPersonalTrainer).map(l => l.length),
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(168, 85, 247, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(14, 165, 233, 0.8)',
        'rgba(34, 197, 94, 0.8)',
      ],
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  // Grafico a barre per guadagni Personal Trainer
  const barChartData = {
    labels: Object.keys(lezioniPerPersonalTrainer),
    datasets: [{
      label: 'Guadagno (€)',
      data: Object.entries(lezioniPerPersonalTrainer).map(([_, lezioni]) => 
        lezioni.length * (lezioni[0]?.personalTrainer?.costo || 0)
      ),
      backgroundColor: 'rgba(251, 191, 36, 0.8)',
      borderColor: 'rgba(251, 191, 36, 1)',
      borderWidth: 1,
      borderRadius: 8,
    }],
  };

  // Grafico distribuzione pacchetti per tipo
  const packageTypeData = {
    labels: ['Personal Training', 'Pilates', 'Consulenza'],
    datasets: [{
      label: 'Numero pacchetti',
      data: [
        pacchettiPersonal.length,
        pacchettiPilates.length,
        pacchettiConsulenza.length
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(16, 185, 129, 0.8)',
      ],
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  // Grafico revenue per tipo
  const revenueTypeData = {
    labels: ['Personal Training', 'Pilates', 'Consulenza'],
    datasets: [{
      label: 'Revenue (€)',
      data: [
        pacchettiPersonal.reduce((acc, p) => acc + p.totale, 0),
        pacchettiPilates.reduce((acc, p) => acc + p.totale, 0),
        pacchettiConsulenza.reduce((acc, p) => acc + p.totale, 0),
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(16, 185, 129, 0.8)',
      ],
      borderRadius: 8,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: { size: 11 }
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0, 0, 0, 0.05)' } },
      x: { grid: { display: false } }
    }
  };

  // Grafico Incassi vs Costi
  const incassiVsCostiData = {
    labels: ['Totale Incassato', 'Costi PT', 'Margine Netto'],
    datasets: [{
      label: 'Importo (€)',
      data: [totaleIncassato, costoPersonalTrainer, margineNetto],
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(59, 130, 246, 0.8)',
      ],
      borderRadius: 8,
    }],
  };

  // Grafico profitto per PT con ricavi e costi
  const profittoPTData = {
    labels: profittoPerPT.map(pt => pt.nome),
    datasets: [
      {
        label: 'Ricavo Generato (€)',
        data: profittoPerPT.map(pt => pt.ricavoGenerato || 0),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderRadius: 6,
      },
      {
        label: 'Costo PT (€)',
        data: profittoPerPT.map(pt => pt.costoTotale || 0),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderRadius: 6,
      }
    ],
  };

  const stackedBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: { size: 11 }
        }
      }
    },
    scales: {
      y: { 
        beginAtZero: true, 
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        stacked: false
      },
      x: { 
        grid: { display: false },
        stacked: false
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header con Filtri Data */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900">Dashboard Statistiche</h1>
            <p className="mt-1 text-sm text-zinc-600">Monitora le performance del tuo centro</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl shadow-sm border border-zinc-200 p-4">
            <span className="text-sm font-medium text-zinc-700">Dal</span>
            <input 
              type="date" 
              value={dal} 
              onChange={(e) => setDal(e.target.value)}
              className="px-3 py-2 bg-zinc-50 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            />
            <span className="text-sm font-medium text-zinc-700">al</span>
            <input 
              type="date" 
              value={al} 
              onChange={(e) => setAl(e.target.value)}
              className="px-3 py-2 bg-zinc-50 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            />
            <button 
              onClick={refresh}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-zinc-900 font-semibold rounded-lg text-sm shadow-sm hover:shadow transition-all"
            >
              <StatsUpSquare className="w-4 h-4" />
              Aggiorna
            </button>
          </div>
        </div>

        {/* Cards Statistiche Principali */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Card Lezioni Totali */}
          <div className="group bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 hover:shadow-lg hover:bg-gradient-to-br hover:from-blue-50 hover:to-white transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                Lezioni
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-600">Lezioni Totali</p>
              <p className="text-3xl font-bold text-zinc-900">{totaleLezioni}</p>
            </div>
          </div>

          {/* Card Guadagni PT */}
          <div className="group bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 hover:shadow-lg hover:bg-gradient-to-br hover:from-green-50 hover:to-white transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors">
                <GraphUp className="w-6 h-6 text-green-600" />
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                PT
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-600">Guadagni Trainer</p>
              <p className="text-3xl font-bold text-zinc-900">{totaleGuadagniPersonalTrainer.toFixed(2)} €</p>
            </div>
          </div>

          {/* Card Pacchetti Venduti */}
          <div className="group bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 hover:shadow-lg hover:bg-gradient-to-br hover:from-yellow-50 hover:to-white transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-100 rounded-xl group-hover:bg-yellow-200 transition-colors">
                <StatsUpSquare className="w-6 h-6 text-yellow-600" />
              </div>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                Vendite
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-600">Pacchetti Venduti</p>
              <p className="text-3xl font-bold text-zinc-900">{pacchettiAcquistati?.length || 0}</p>
              <p className="text-xs text-zinc-500 mt-1">Revenue: {totalePacchettiAcquistati.toFixed(2)} €</p>
            </div>
          </div>

          {/* Card Incassi */}
          <div className="group bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 hover:shadow-lg hover:bg-gradient-to-br hover:from-purple-50 hover:to-white transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                <Wallet className="w-6 h-6 text-purple-600" />
              </div>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                Incassi
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-600">Totale Incassato</p>
              <p className="text-3xl font-bold text-zinc-900">{totaleVersamenti.toFixed(2)} €</p>
              <p className="text-xs text-zinc-500 mt-1">N° versamenti: {versamenti?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Cards Analisi Finanziaria */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Card Costi PT */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <Wallet className="w-6 h-6 text-red-600" />
              </div>
              <span className="text-xs font-semibold text-red-600">COSTI</span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-600">Costi Personal Trainer</p>
              <p className="text-2xl font-bold text-red-600">- {costoPersonalTrainer.toFixed(2)} €</p>
              <p className="text-xs text-zinc-500 mt-1">Pagamenti ai trainer</p>
            </div>
          </div>

          {/* Card Margine Netto */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <GraphUp className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-xs font-semibold text-blue-600">PROFITTO</span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-600">Margine Netto</p>
              <p className="text-2xl font-bold text-blue-600">{margineNetto.toFixed(2)} €</p>
              <p className="text-xs text-zinc-500 mt-1">Incassi - Costi PT</p>
            </div>
          </div>

          {/* Card Margine % */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <StatsUpSquare className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs font-semibold text-green-600">MARGINE</span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-600">Margine %</p>
              <p className="text-2xl font-bold text-green-600">{marginePercentuale.toFixed(1)} %</p>
              <p className="text-xs text-zinc-500 mt-1">Percentuale di profitto</p>
            </div>
          </div>
        </div>

        {/* Sezione Grafici */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Grafico Incassi vs Costi */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-zinc-900">Analisi Finanziaria</h3>
              <p className="text-sm text-zinc-600">Confronto incassi, costi e margine netto</p>
            </div>
            <div className="h-80">
              <Bar data={incassiVsCostiData} options={barChartOptions} />
            </div>
          </div>

          {/* Grafico Ricavi vs Costi per PT */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-zinc-900">Ricavi vs Costi per Personal Trainer</h3>
              <p className="text-sm text-zinc-600">Confronto tra ricavo generato e costo sostenuto</p>
            </div>
            <div className="h-80">
              <Bar data={profittoPTData} options={stackedBarOptions} />
            </div>
          </div>

          {/* Grafico Lezioni per Personal Trainer */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <h3 className="text-lg font-semibold text-zinc-900 mb-4">Lezioni per Personal Trainer</h3>
            <div className="h-80">
              <Pie data={pieChartData} options={chartOptions} />
            </div>
          </div>

          {/* Grafico Guadagni per Personal Trainer */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <h3 className="text-lg font-semibold text-zinc-900 mb-4">Costi per Personal Trainer</h3>
            <div className="h-80">
              <Bar data={barChartData} options={barChartOptions} />
            </div>
          </div>

          {/* Grafico Distribuzione Pacchetti */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <h3 className="text-lg font-semibold text-zinc-900 mb-4">Distribuzione Pacchetti per Tipo</h3>
            <div className="h-80">
              <Pie data={packageTypeData} options={chartOptions} />
            </div>
          </div>

          {/* Grafico Revenue per Tipo */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
            <h3 className="text-lg font-semibold text-zinc-900 mb-4">Revenue per Tipo Pacchetto</h3>
            <div className="h-80">
              <Bar data={revenueTypeData} options={barChartOptions} />
            </div>
          </div>
        </div>

        {/* Tabelle Dettagliate */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Tabella Pacchetti Acquistati */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden">
            <div className="p-6 border-b border-zinc-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900">Pacchetti Acquistati</h3>
                  <p className="text-sm text-zinc-600 mt-1">
                    {pacchettiFiltered.length} pacchetti • Totale: {pacchettiFiltered.reduce((acc, p) => acc + p.totale, 0).toFixed(2)} €
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFiltroPacchetti('tutti')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroPacchetti === 'tutti'
                        ? 'bg-zinc-900 text-white'
                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                    }`}
                  >
                    Tutti
                  </button>
                  <button
                    onClick={() => setFiltroPacchetti('personal')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroPacchetti === 'personal'
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                  >
                    Personal
                  </button>
                  <button
                    onClick={() => setFiltroPacchetti('pilates')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroPacchetti === 'pilates'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                    }`}
                  >
                    Pilates
                  </button>
                  <button
                    onClick={() => setFiltroPacchetti('consulenza')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroPacchetti === 'consulenza'
                        ? 'bg-green-500 text-white'
                        : 'bg-green-100 text-green-600 hover:bg-green-200'
                    }`}
                  >
                    Consulenza
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50 sticky top-0 z-10">
                    <tr className="text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      <th className="px-6 py-3">Cliente</th>
                      <th className="px-6 py-3">Data</th>
                      <th className="px-6 py-3">Tipo</th>
                      <th className="px-6 py-3 text-right">Importo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {pacchettiFiltered?.map((item, index) => (
                      <tr key={index} className="hover:bg-zinc-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-zinc-900">
                          {item?.utenti?.nome} {item?.utenti?.cognome}
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600">
                          {item?.created_at ? new Date(item?.created_at).toLocaleDateString('it-IT') : "-"}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item?.tipo === 0 ? 'bg-blue-100 text-blue-700' :
                            item?.tipo === 1 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {item?.tipo === 0 ? "Personal" : item?.tipo === 1 ? "Pilates" : "Consulenza"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-zinc-900 text-right">
                          {item?.totale} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Tabella Versamenti */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden">
            <div className="p-6 border-b border-zinc-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900">Versamenti Effettuati</h3>
                  <p className="text-sm text-zinc-600 mt-1">
                    {versamentiFiltered.length} versamenti • Totale: {versamentiFiltered.reduce((acc, v) => acc + v.importo, 0).toFixed(2)} €
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFiltroVersamenti('tutti')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroVersamenti === 'tutti'
                        ? 'bg-zinc-900 text-white'
                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                    }`}
                  >
                    Tutti
                  </button>
                  <button
                    onClick={() => setFiltroVersamenti('personal')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroVersamenti === 'personal'
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                  >
                    Personal
                  </button>
                  <button
                    onClick={() => setFiltroVersamenti('pilates')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroVersamenti === 'pilates'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                    }`}
                  >
                    Pilates
                  </button>
                  <button
                    onClick={() => setFiltroVersamenti('consulenza')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filtroVersamenti === 'consulenza'
                        ? 'bg-green-500 text-white'
                        : 'bg-green-100 text-green-600 hover:bg-green-200'
                    }`}
                  >
                    Consulenza
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50 sticky top-0 z-10">
                    <tr className="text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                      <th className="px-6 py-3">Cliente</th>
                      <th className="px-6 py-3">Data</th>
                      <th className="px-6 py-3">Pacchetto</th>
                      <th className="px-6 py-3 text-right">Importo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {versamentiFiltered?.map((item, index) => (
                      <tr key={index} className="hover:bg-zinc-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-zinc-900">
                          {item?.utenti?.nome} {item?.utenti?.cognome}
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600">
                          {item?.data ? new Date(item?.data).toLocaleDateString('it-IT') : "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600">
                          {item?.pacchettiAcquistati?.pacchetti?.nome || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-green-600 text-right">
                          {item?.importo} €
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Tabella Performance Personal Trainer - Profitto Reale */}
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden lg:col-span-2">
            <div className="p-6 border-b border-zinc-200">
              <h3 className="text-lg font-semibold text-zinc-900">Performance Personal Trainer - Analisi Profitto</h3>
              <p className="text-sm text-zinc-600 mt-1">Ricavo generato vs costi sostenuti per ogni trainer</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr className="text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    <th className="px-6 py-3">Personal Trainer</th>
                    <th className="px-6 py-3 text-center">Lezioni</th>
                    <th className="px-6 py-3 text-right">Ricavo Generato</th>
                    <th className="px-6 py-3 text-right">Costo PT</th>
                    <th className="px-6 py-3 text-right">Profitto Netto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {profittoPerPT.map((pt, index) => (
                    <tr key={index} className="hover:bg-zinc-50 transition-colors cursor-pointer" onClick={() => navigate(`/profilopersonaltrainer/${pt.id}`)}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline">
                            {pt.nome}
                          </span>
                          <OpenInWindow className="w-4 h-4 text-blue-400" />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-900 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">
                          {pt.lezioni}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-zinc-900 text-right">
                        {(pt.ricavoGenerato || 0).toFixed(2)} €
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-red-600 text-right">
                        {(pt.costoTotale || 0).toFixed(2)} €
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-right">
                        <span className={pt.profittoNetto >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {(pt.profittoNetto || 0).toFixed(2)} €
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-zinc-100 font-bold">
                    <td className="px-6 py-4 text-sm text-zinc-900">TOTALE</td>
                    <td className="px-6 py-4 text-sm text-zinc-900 text-center">
                      {profittoPerPT.reduce((acc, pt) => acc + pt.lezioni, 0)}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-zinc-900 text-right">
                      {(profittoPerPT.reduce((acc, pt) => acc + (pt.ricavoGenerato || 0), 0)).toFixed(2)} €
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-red-600 text-right">
                      {(profittoPerPT.reduce((acc, pt) => acc + (pt.costoTotale || 0), 0)).toFixed(2)} €
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-green-600 text-right">
                      {(profittoPerPT.reduce((acc, pt) => acc + (pt.profittoNetto || 0), 0)).toFixed(2)} €
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="p-6 border-t border-zinc-200 bg-zinc-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm font-medium text-zinc-600">Ricavo Totale PT</p>
                  <p className="text-xl font-bold text-zinc-900 mt-1">
                    {(profittoPerPT.reduce((acc, pt) => acc + (pt.ricavoGenerato || 0), 0)).toFixed(2)} €
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-600">Costi Totali PT</p>
                  <p className="text-xl font-bold text-red-600 mt-1">
                    {(profittoPerPT.reduce((acc, pt) => acc + (pt.costoTotale || 0), 0)).toFixed(2)} €
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-600">Margine Netto da PT</p>
                  <p className="text-xl font-bold text-green-600 mt-1">
                    {(profittoPerPT.reduce((acc, pt) => acc + (pt.profittoNetto || 0), 0)).toFixed(2)} €
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Analisi;